import React, { useEffect, useState } from 'react';
import Button from '../../bootstrap/Button';
import FormGroup from '../../bootstrap/forms/FormGroup';
import Input from '../../bootstrap/forms/Input';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../bootstrap/Modal';
import showNotification from '../../extras/showNotification';
import { changePassword, requestPasswordCode, storeNewToken } from '../../../services/account/account-service';
import { checkNewPassword } from '../../../services/account/password-rules';

interface Props {
	isOpen: boolean;
	setIsOpen(value: boolean): void;
}

type Step = 'request' | 'confirm';

export const ChangePasswordModal: React.FC<Props> = ({ isOpen, setIsOpen }) => {
	const [step, setStep] = useState<Step>('request');
	const [sentTo, setSentTo] = useState('');
	const [resendIn, setResendIn] = useState(0);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [fields, setFields] = useState({ current: '', code: '', next: '', confirm: '' });

	useEffect(() => {
		if (resendIn <= 0) return undefined;
		const timer = setTimeout(() => setResendIn(resendIn - 1), 1000);
		return () => clearTimeout(timer);
	}, [resendIn]);

	const close = () => {
		// An in-flight request must not resolve on a dismissed modal.
		if (busy) return;
		setIsOpen(false);
		setStep('request');
		setSentTo('');
		setResendIn(0);
		setError(null);
		setFields({ current: '', code: '', next: '', confirm: '' });
	};

	const sendCode = async () => {
		setBusy(true);
		setError(null);
		const result = await requestPasswordCode();
		setBusy(false);
		if (!result.ok) {
			setError(result.message);
			if (result.retryAfter) setResendIn(result.retryAfter);
			return;
		}
		setSentTo(result.data.sent_to);
		setResendIn(result.data.resend_after);
		setStep('confirm');
	};

	const submit = async () => {
		const ruleError = checkNewPassword(fields.next, fields.confirm);
		if (ruleError) {
			setError(ruleError);
			return;
		}
		setBusy(true);
		setError(null);
		const result = await changePassword({ currentPassword: fields.current, code: fields.code, newPassword: fields.next });
		setBusy(false);
		if (!result.ok) {
			const suffix = result.attemptsLeft !== undefined ? ` Restam ${result.attemptsLeft} tentativa(s).` : '';
			setError(result.message + suffix);
			return;
		}
		storeNewToken(result.data.token);
		showNotification('Senha alterada.', '', 'success');
		close();
	};

	// Legacy accounts may have a non-numeric current password; the backend validates it.
	const onField = (name: keyof typeof fields, digitsOnly = true) => (e: React.ChangeEvent<HTMLInputElement>) =>
		setFields({ ...fields, [name]: digitsOnly ? e.target.value.replace(/\D/g, '') : e.target.value });

	return (
		<Modal isOpen={isOpen} setIsOpen={close} titleId='change-password-title'>
			<ModalHeader setIsOpen={close}>
				<ModalTitle id='change-password-title'>Trocar senha</ModalTitle>
			</ModalHeader>
			<ModalBody>
				{step === 'request' && <p>Vamos enviar um código de verificação para o seu WhatsApp.</p>}
				{step === 'confirm' && (
					<div className='row g-3'>
						<p className='col-12'>Enviamos um código para {sentTo}.</p>
						<FormGroup className='col-12' id='current-password' label='Senha atual'>
							<Input type='password' autoComplete='current-password' value={fields.current} onChange={onField('current', false)} />
						</FormGroup>
						<FormGroup className='col-12' id='otp-code' label='Código recebido'>
							<Input type='text' inputMode='numeric' autoComplete='one-time-code' value={fields.code} onChange={onField('code')} />
						</FormGroup>
						<FormGroup className='col-12' id='new-password' label='Nova senha (6 a 8 dígitos)'>
							<Input type='password' inputMode='numeric' autoComplete='new-password' value={fields.next} onChange={onField('next')} />
						</FormGroup>
						<FormGroup className='col-12' id='confirm-password' label='Confirmar nova senha'>
							<Input type='password' inputMode='numeric' autoComplete='new-password' value={fields.confirm} onChange={onField('confirm')} />
						</FormGroup>
					</div>
				)}
				{error && <div role='alert' className='text-danger mt-3'>{error}</div>}
			</ModalBody>
			<ModalFooter>
				{step === 'request' && (
					<Button color='primary' isDisable={busy} onClick={sendCode}>
						Enviar código
					</Button>
				)}
				{step === 'confirm' && (
					<>
						<Button color='link' isDisable={busy || resendIn > 0} onClick={sendCode}>
							{resendIn > 0 ? `Reenviar código em ${resendIn}s` : 'Reenviar código'}
						</Button>
						<Button color='primary' isDisable={busy || fields.code.length !== 6} onClick={submit}>
							Trocar senha
						</Button>
					</>
				)}
			</ModalFooter>
		</Modal>
	);
};
